// Dashboard API helpers
import API_URL from '../config/api';
import { apiFetch } from '../utils/apiFetch';

const OPEN_ORDER_STATUSES = new Set(['Draft', 'Confirmed', 'Processing', 'Pending']);

async function tryFetch(url, token) {
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const res = await apiFetch(url, { credentials: 'include', headers });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

function invoiceRemaining(inv) {
  const amount = Number(inv.amount || 0);
  const paid = Number(inv.paid_amount ?? inv.paidAmount ?? 0);
  if (inv.remaining_balance != null && inv.remaining_balance !== '') {
    return Math.max(0, Number(inv.remaining_balance));
  }
  return Math.max(0, amount - paid);
}

function isInvoicePaid(inv) {
  if (String(inv.status || '') === 'Paid') return true;
  return invoiceRemaining(inv) <= 0;
}

function productQty(p) {
  return Number(p.quantity ?? p.stock ?? 0);
}

function productMinStock(p) {
  return Number(p.minStock ?? p.min_stock ?? 10);
}

function isLowStockProduct(p) {
  if (p.deleted_at) return false;
  return productQty(p) <= productMinStock(p);
}

function isOpenWholesaleOrder(o) {
  return OPEN_ORDER_STATUSES.has(o.status || '');
}

function orderTimestamp(o) {
  const raw = o.created_at || o.createdAt || o.orderDate || o.order_date;
  return raw ? new Date(raw) : null;
}

/** Build dashboard payload from existing list endpoints (fallback if /dashboard/wholesale fails) */
async function fetchDashboardFallback(token) {
  const headers = { Authorization: `Bearer ${token}` };
  const fetchJson = async (url) => {
    const res = await apiFetch(url, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status} ${url}`);
    return res.json();
  };

  const [business, orders, invoices, products, customers, analytics] = await Promise.all([
    fetchJson(`${API_URL}/business`).catch(() => null),
    fetchJson(`${API_URL}/orders`).catch(() => []),
    fetchJson(`${API_URL}/invoices`).catch(() => []),
    fetchJson(`${API_URL}/products`).catch(() => []),
    fetchJson(`${API_URL}/customers`).catch(() => []),
    fetchJson(`${API_URL}/analytics/dashboard`).catch(() => null),
  ]);

  const allOrders = Array.isArray(orders) ? orders : [];
  const allInvoices = Array.isArray(invoices) ? invoices : [];
  const allProducts = Array.isArray(products) ? products : [];
  const allCustomers = Array.isArray(customers) ? customers : [];

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayTime = today.getTime();

  const thisMonth = today.getMonth();
  const thisYear = today.getFullYear();
  const lastMonthDate = new Date(today);
  lastMonthDate.setMonth(lastMonthDate.getMonth() - 1);

  const monthRevenue = allOrders
    .filter((o) => {
      const d = orderTimestamp(o);
      return d && d.getMonth() === thisMonth && d.getFullYear() === thisYear;
    })
    .reduce((s, o) => s + Number(o.total || 0), 0);

  const lastMonthRevenue = allOrders
    .filter((o) => {
      const d = orderTimestamp(o);
      return d && d.getMonth() === lastMonthDate.getMonth() && d.getFullYear() === lastMonthDate.getFullYear();
    })
    .reduce((s, o) => s + Number(o.total || 0), 0);

  const openInvoices = allInvoices.filter((inv) => !isInvoicePaid(inv));
  let totalAR = 0;
  let overdueCount = 0;
  const arAging = { current: 0, d30: 0, d60: 0, d90plus: 0 };

  for (const inv of openInvoices) {
    const remaining = invoiceRemaining(inv);
    if (remaining <= 0) continue;
    totalAR += remaining;
    const dueRaw = inv.due_date || inv.dueDate;
    const due = dueRaw ? new Date(dueRaw) : null;
    if (due && !Number.isNaN(due.getTime()) && due.getTime() < todayTime) overdueCount += 1;
    if (!due || Number.isNaN(due.getTime())) {
      arAging.current += remaining;
      continue;
    }
    due.setHours(0, 0, 0, 0);
    const days = Math.floor((todayTime - due.getTime()) / 86400000);
    if (days <= 0) arAging.current += remaining;
    else if (days <= 30) arAging.d30 += remaining;
    else if (days <= 60) arAging.d60 += remaining;
    else arAging.d90plus += remaining;
  }

  const revenueHistoryFromOrders = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    d.setHours(0, 0, 0, 0);
    const dt = d.getTime();
    revenueHistoryFromOrders.push(
      allOrders
        .filter((o) => {
          const od = orderTimestamp(o);
          if (!od) return false;
          od.setHours(0, 0, 0, 0);
          return od.getTime() === dt;
        })
        .reduce((s, o) => s + Number(o.total || 0), 0)
    );
  }

  const revenueHistory = revenueHistoryFromOrders.some((v) => v > 0)
    ? revenueHistoryFromOrders
    : Array.isArray(analytics?.charts?.revenueByDay)
      ? analytics.charts.revenueByDay.map((d) => Number(d.revenue ?? 0))
      : revenueHistoryFromOrders;

  const recentOrders = allOrders
    .slice()
    .sort((a, b) => {
      const ta = orderTimestamp(a)?.getTime() || Number(a.id || 0);
      const tb = orderTimestamp(b)?.getTime() || Number(b.id || 0);
      return tb - ta;
    })
    .slice(0, 5)
    .map((o) => ({
      id: o.id,
      order_number: o.order_number || o.orderNumber,
      total: o.total,
      status: o.status,
      customer: o.customer || o.customerName || o.customer_name || 'Customer',
      created_at: o.created_at || o.createdAt,
    }));

  const customerSpend = {};
  allOrders.forEach((o) => {
    const cid = o.customer_id || o.customerId;
    if (!cid) return;
    customerSpend[cid] = (customerSpend[cid] || 0) + Number(o.total || 0);
  });

  const topCustomers = allCustomers
    .map((c) => ({ id: c.id, name: c.name, total: customerSpend[c.id] || 0 }))
    .filter((c) => c.total > 0)
    .sort((a, b) => b.total - a.total)
    .slice(0, 5);

  const lowStockList = allProducts
    .filter(isLowStockProduct)
    .sort((a, b) => productQty(a) - productQty(b))
    .slice(0, 5)
    .map((p) => ({
      id: p.id,
      name: p.name,
      sku: p.sku,
      quantity: productQty(p),
      stock: productQty(p),
      min_stock: productMinStock(p),
      minStock: productMinStock(p),
    }));

  const metrics = {
    todayRevenue: allOrders
      .filter((o) => {
        const d = orderTimestamp(o);
        if (!d) return false;
        d.setHours(0, 0, 0, 0);
        return d.getTime() === todayTime;
      })
      .reduce((s, o) => s + Number(o.total || 0), 0),
    monthRevenue,
    lastMonthRevenue,
    pendingOrders: allOrders.filter(isOpenWholesaleOrder).length,
    overdueInvoices: overdueCount,
    lowStockProducts: allProducts.filter(isLowStockProduct).length,
    expiringProducts: allProducts.filter((p) => {
      if (p.deleted_at) return false;
      const ed = p.expiry_date || p.expiryDate;
      if (!ed) return false;
      const exp = new Date(ed);
      if (Number.isNaN(exp.getTime())) return false;
      exp.setHours(0, 0, 0, 0);
      return Math.ceil((exp.getTime() - todayTime) / 86400000) <= 30;
    }).length,
    totalAR,
    todayBookingsCount: 0,
  };

  const alerts = [];
  if (metrics.overdueInvoices > 0) {
    alerts.push({ icon: '🚨', message: `${metrics.overdueInvoices} overdue invoice${metrics.overdueInvoices > 1 ? 's' : ''}`, path: '/invoices', tone: 'red' });
  }
  if (metrics.lowStockProducts > 0) {
    alerts.push({ icon: '📦', message: `${metrics.lowStockProducts} low stock item${metrics.lowStockProducts > 1 ? 's' : ''}`, path: '/products/list', tone: 'amber' });
  }

  return {
    business,
    metrics,
    arAging,
    revenueHistory,
    recentOrders,
    topCustomers,
    lowStockList,
    todayBookings: [],
    alerts,
    suggestions: [],
    _fallback: true,
  };
}

/** Single round-trip wholesale dashboard payload; falls back to legacy fetches if needed */
export async function fetchWholesaleDashboard(token) {
  try {
    return await tryFetch(`${API_URL}/dashboard/wholesale`, token);
  } catch (primaryErr) {
    console.warn('Dashboard wholesale API failed, using fallback:', primaryErr);
    return fetchDashboardFallback(token);
  }
}

export async function fetchStats(token) {
  try {
    const data = await tryFetch(`${API_URL}/analytics/dashboard`, token);
    return {
      revenue: data.kpis?.totalRevenue ?? 0,
      orders: data.kpis?.totalOrders ?? data.kpis?.totalBookings ?? 0,
      invoices: data.kpis?.totalInvoices ?? 0,
      customers: data.kpis?.totalCustomers ?? 0,
      revenueHistory: Array.isArray(data.charts?.revenueByDay)
        ? data.charts.revenueByDay.map((d) => d.revenue ?? 0)
        : Object.values(data.charts?.revenueByDay ?? {}),
    };
  } catch (e) {
    console.error('Dashboard stats fetch failed:', e);
    return {
      revenue: 0,
      orders: 0,
      invoices: 0,
      customers: 0,
      revenueHistory: [],
      fetchError: true,
    };
  }
}

export async function fetchRecentOrders(token) {
  try {
    const orders = await tryFetch(`${API_URL}/orders`, token);
    if (!Array.isArray(orders)) return [];
    const sorted = orders.slice().sort((a, b) => {
      const ta = new Date(a.createdAt || a.orderDate || a.created_at || 0).getTime() || Number(a.id || 0);
      const tb = new Date(b.createdAt || b.orderDate || b.created_at || 0).getTime() || Number(b.id || 0);
      return tb - ta;
    });
    return sorted.slice(0, 5);
  } catch (e) {
    console.error('Dashboard recent orders fetch failed:', e);
    return [];
  }
}

export async function fetchRecentInvoices(token) {
  try {
    const invoices = await tryFetch(`${API_URL}/invoices`, token);
    if (!Array.isArray(invoices)) return [];
    const sorted = invoices.slice().sort((a, b) => {
      const ta = new Date(a.createdAt || a.created_at || 0).getTime() || Number(a.id || 0);
      const tb = new Date(b.createdAt || b.created_at || 0).getTime() || Number(b.id || 0);
      return tb - ta;
    });
    return sorted.slice(0, 5);
  } catch (e) {
    console.error('Dashboard recent invoices fetch failed:', e);
    return [];
  }
}

export default {
  fetchWholesaleDashboard,
  fetchStats,
  fetchRecentOrders,
  fetchRecentInvoices,
};
